import { useState } from 'react'
import { Icon } from '@carrent/ui'
import { carFieldLabel, carFieldValue, describeCarEvent } from '@carrent/domain'

/**
 * Список ручных изменений машины.
 *
 * Общий для вкладки на карточке и для боковой панели в списке. Вынесен
 * ровно потому, что вторая копия разошлась бы с первой: панель и вкладка
 * показывают одно и то же событие, и различие в формулировке или в разборе
 * значений выглядело бы как разные данные.
 *
 * Компонент не грузит данные: у вкладки пагинация и своя страница,
 * у панели — последние десять. Загрузка остаётся у того, кто знает,
 * сколько ему нужно.
 */
export function CarChangeList({ entries, dictionaries, compact = false }) {
  const [openId, setOpenId] = useState(null)

  return (
    <ol className={`history__list ${compact ? 'history__list--compact' : ''}`.trim()}>
      {entries.map((entry) => (
        <ChangeEntry
          key={entry.id}
          entry={entry}
          dictionaries={dictionaries}
          open={openId === entry.id}
          onToggle={() => setOpenId((id) => (id === entry.id ? null : entry.id))}
        />
      ))}
    </ol>
  )
}

function ChangeEntry({ entry, dictionaries, open, onToggle }) {
  const { title } = describeCarEvent(entry)
  const fields = Object.keys(entry.after || {})
  const when = new Date(entry.created_at)

  return (
    <li className="history__item">
      <button type="button" className="history__head" onClick={onToggle} aria-expanded={open}>
        <Icon
          name="chevronDown"
          size={14}
          className={open ? 'history__caret history__caret--open' : 'history__caret'}
        />
        <span className="history__title">{title}</span>
        <span className="history__actor">
          {entry.actor ? entry.actor.full_name : 'Система'}
        </span>
        <time
          className="history__time"
          dateTime={entry.created_at}
          title={when.toLocaleString('ru', { dateStyle: 'full', timeStyle: 'medium' })}
        >
          {when.toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' })}
        </time>
      </button>

      {open && (
        <dl className="history__changes">
          {fields.length === 0 && <p className="history__empty">Значения не записаны.</p>}
          {fields.map((field) => (
            <div key={field} className="history__change">
              <dt className="history__field">{carFieldLabel(field)}</dt>
              <dd className="history__values">
                <span className="history__was">
                  {/* Знак, а не только цвет: различие обязано читаться
                      и в чёрно-белой печати, и при дальтонизме. */}
                  <span aria-hidden="true">−</span>
                  {carFieldValue(field, entry.before?.[field], dictionaries)}
                </span>
                <span className="history__became">
                  <span aria-hidden="true">+</span>
                  {carFieldValue(field, entry.after[field], dictionaries)}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  )
}

/**
 * Подпись авторства: «Завёл X · изменил Y, N назад».
 *
 * Тот же приём, что в Jira и Confluence: две строки под заголовком
 * отвечают на «чья это запись и свежая ли она» без разворота истории.
 * Клик по имени ведёт в карточку пользователя — оттуда видна вся его
 * активность, а не только эта машина.
 */
export function AuthorshipLine({ authorship, onActor }) {
  if (!authorship) return null
  const { created, updated } = authorship
  if (!created && !updated) return null

  return (
    <p className="authorship">
      {created && (
        <span className="authorship__part">
          Завёл{' '}
          <ActorName entry={created} onActor={onActor} />
          <time dateTime={created.created_at}
            title={new Date(created.created_at).toLocaleString('ru',
              { dateStyle: 'full', timeStyle: 'medium' })}>
            {' '}{formatAgo(new Date(created.created_at))}
          </time>
        </span>
      )}
      {created && updated && <span aria-hidden="true"> · </span>}
      {updated && (
        <span className="authorship__part">
          Изменил{' '}
          <ActorName entry={updated} onActor={onActor} />
          <time dateTime={updated.created_at}
            title={new Date(updated.created_at).toLocaleString('ru',
              { dateStyle: 'full', timeStyle: 'medium' })}>
            {' '}{formatAgo(new Date(updated.created_at))}
          </time>
        </span>
      )}
    </p>
  )
}

function ActorName({ entry, onActor }) {
  if (!entry.actor) return <span>система</span>
  if (!onActor || !entry.actor.id) return <span>{entry.actor.full_name}</span>
  return (
    <button type="button" className="authorship__actor"
      onClick={() => onActor(entry.actor.id)}>
      {entry.actor.full_name}
    </button>
  )
}

/**
 * Относительное время.
 *
 * «5 минут назад» отвечает на вопрос «свежая ли правка» без вычитания
 * в уме. Точная дата остаётся в подсказке: она нужна, но реже.
 */
export function formatAgo(date, now = new Date()) {
  const minutes = Math.floor((now - date) / 60000)

  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин назад`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} ч назад`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} дн. назад`

  return date.toLocaleDateString('ru', { dateStyle: 'medium' })
}
